import {
  ArrowLeftOutlined,
  BookOutlined,
  BulbOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseCircleFilled,
  ExperimentOutlined,
  FileTextOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  SearchOutlined,
  StarFilled,
  TagOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { Column, Line } from "@ant-design/plots";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Skeleton,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import PageLayout from "components/common/pageLayout/pageLayout";
import moment from "moment";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAnalyticsDetailQuery } from "../api";
import { ANALYTICS_DETAIL_MOCK } from "../mockData";
import { ANALYTICS_USE_MOCK_DATA } from "utils/featureFlags";
import styles from "./AnalyticsDetailPage.module.less";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const PASS_THRESHOLD = 60;

const ATTRIBUTE_CONFIG = {
  specialty:   { label: "Specialty",   plural: "Specialties",  color: "#4b93ff", icon: <ExperimentOutlined /> },
  script:      { label: "Script",      plural: "Scripts",      color: "#8b5cf6", icon: <FileTextOutlined /> },
  topic:       { label: "Topic",       plural: "Topics",       color: "#16a34a", icon: <TagOutlined /> },
  explanation: { label: "Explanation", plural: "Explanations", color: "#d97706", icon: <BookOutlined /> },
};

const DATE_PRESETS = [
  { label: "Last 30 days",  value: [moment().subtract(30, "days"),  moment()] },
  { label: "Last 3 months", value: [moment().subtract(3, "months"), moment()] },
  { label: "Last 6 months", value: [moment().subtract(6, "months"), moment()] },
  { label: "This year",     value: [moment().startOf("year"),       moment()] },
  { label: "All time",      value: [moment("2020-01-01"),           moment()] },
];

const BUCKETS = [
  { label: "0–19%",   min: 0,  max: 19,  color: "#ef4444" },
  { label: "20–39%",  min: 20, max: 39,  color: "#f97316" },
  { label: "40–59%",  min: 40, max: 59,  color: "#d97706" },
  { label: "60–79%",  min: 60, max: 79,  color: "#4b93ff" },
  { label: "80–100%", min: 80, max: 100, color: "#16a34a" },
];

const buildDistribution = (quizHistory) =>
  BUCKETS.map((b) => ({
    range: b.label,
    count: quizHistory.filter((q) => q.accuracy >= b.min && q.accuracy <= b.max).length,
    color: b.color,
  }));

// ── Question-level aggregation ────────────────────────────────────────────────
// Rolls up all questions across every quiz in the history into one row per
// unique question ID so we can see cross-quiz mastery.
const Q_TAGS = {
  mastered:     { label: "Mastered",     color: "success",  hex: "#16a34a", bg: "#f0fff4" },
  progressing:  { label: "Progressing",  color: "processing", hex: "#4b93ff", bg: "#eff6ff" },
  inconsistent: { label: "Inconsistent", color: "warning",  hex: "#d97706", bg: "#fffbeb" },
  needs_review: { label: "Needs Review", color: "error",    hex: "#ef4444", bg: "#fff5f5" },
};

const tagKey = (acc) =>
  acc >= 80 ? "mastered" : acc >= 60 ? "progressing" : acc >= 40 ? "inconsistent" : "needs_review";

const aggregateQuestions = (quizHistory) => {
  const map = {};
  quizHistory.forEach((quiz) => {
    (quiz.questions ?? []).forEach((q) => {
      if (!map[q.id]) {
        map[q.id] = { id: q.id, text: q.text, attempts: 0, correct: 0, wrong: 0 };
      }
      map[q.id].attempts++;
      if (q.isCorrect) map[q.id].correct++;
      else              map[q.id].wrong++;
    });
  });
  return Object.values(map).map((q) => {
    const accuracy = Math.round((q.correct / q.attempts) * 100);
    return { ...q, accuracy, tag: tagKey(accuracy) };
  });
};

// ── Questions Analysis section ────────────────────────────────────────────────
const QuestionsAnalysis = ({ quizHistory, navigate }) => {
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");

  const allQuestions = useMemo(() => aggregateQuestions(quizHistory), [quizHistory]);

  const summary = useMemo(() => ({
    mastered:     allQuestions.filter((q) => q.tag === "mastered").length,
    progressing:  allQuestions.filter((q) => q.tag === "progressing").length,
    inconsistent: allQuestions.filter((q) => q.tag === "inconsistent").length,
    needs_review: allQuestions.filter((q) => q.tag === "needs_review").length,
  }), [allQuestions]);

  const filtered = useMemo(() => {
    let list = allQuestions;
    if (tagFilter !== "all") list = list.filter((q) => q.tag === tagFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => r.text?.toLowerCase().includes(q));
    }
    return list;
  }, [allQuestions, tagFilter, search]);

  const qColumns = [
    {
      title: "Question",
      dataIndex: "text",
      key: "text",
      width: "44%",
      render: (v) => <span className={styles.qText}>{v}</span>,
    },
    {
      title: "Attempts",
      dataIndex: "attempts",
      key: "attempts",
      sorter: (a, b) => a.attempts - b.attempts,
      align: "center",
      width: "9%",
    },
    {
      title: "Correct",
      dataIndex: "correct",
      key: "correct",
      sorter: (a, b) => a.correct - b.correct,
      align: "center",
      width: "9%",
      render: (v) => (
        <span className={styles.answerCorrect}>
          <CheckCircleFilled style={{ marginRight: 3, color: "#16a34a" }} />{v}
        </span>
      ),
    },
    {
      title: "Wrong",
      dataIndex: "wrong",
      key: "wrong",
      sorter: (a, b) => a.wrong - b.wrong,
      align: "center",
      width: "8%",
      render: (v) => (
        <span className={styles.answerWrong}>
          <CloseCircleFilled style={{ marginRight: 3, color: "#ef4444" }} />{v}
        </span>
      ),
    },
    {
      title: "Accuracy",
      dataIndex: "accuracy",
      key: "accuracy",
      sorter: (a, b) => a.accuracy - b.accuracy,
      defaultSortOrder: "ascend",
      width: "16%",
      render: (v) => {
        const color = Q_TAGS[tagKey(v)].hex;
        return (
          <div className={styles.scoreCell}>
            <div className={styles.scoreBarTrack}>
              <div className={styles.scoreBarFill} style={{ width: `${v}%`, backgroundColor: color }} />
            </div>
            <span className={styles.scorePct} style={{ color }}>{v}%</span>
          </div>
        );
      },
    },
    {
      title: "Mastery",
      dataIndex: "tag",
      key: "tag",
      align: "center",
      width: "10%",
      render: (v) => (
        <Tag color={Q_TAGS[v].color}>{Q_TAGS[v].label}</Tag>
      ),
    },
    {
      title: "View",
      key: "view",
      align: "center",
      width: "4%",
      render: (_, r) => (
        <Tooltip title="View this question">
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => navigate(`/script-questions/detail/${r.id}`)}
            className={styles.viewLink}
          />
        </Tooltip>
      ),
    },
  ];

  if (!allQuestions.length) return null;

  return (
    <Card
      className={styles.tableCard}
      title={
        <div className={styles.cardTitle}>
          <BulbOutlined style={{ color: "#8b5cf6" }} />
          <span>Questions Analysis</span>
          <span className={styles.quizCount}>{allQuestions.length} unique questions</span>
          <Tooltip title="Aggregated across all quizzes. Default sort shows weakest first so you can focus your study.">
            <InfoCircleOutlined className={styles.infoIcon} />
          </Tooltip>
        </div>
      }
    >
      {/* ── Summary chips ──────────────────────────────────────────── */}
      <div className={styles.qSummaryStrip}>
        {Object.entries(Q_TAGS).map(([key, cfg]) => (
          <button
            key={key}
            className={`${styles.qSummaryChip} ${tagFilter === key ? styles.qSummaryChipActive : ""}`}
            style={tagFilter === key ? { borderColor: cfg.hex, background: cfg.bg } : {}}
            onClick={() => setTagFilter(tagFilter === key ? "all" : key)}
          >
            <span className={styles.qSummaryDot} style={{ background: cfg.hex }} />
            <span className={styles.qSummaryLabel}>{cfg.label}</span>
            <span className={styles.qSummaryCount} style={{ color: cfg.hex }}>{summary[key]}</span>
          </button>
        ))}
        {tagFilter !== "all" && (
          <button className={styles.qClearFilter} onClick={() => setTagFilter("all")}>
            Clear filter
          </button>
        )}
      </div>

      {/* ── Search ────────────────────────────────────────────────── */}
      <div className={styles.tableSearch}>
        <Input
          placeholder="Search question text…"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 340 }}
        />
        <span className={styles.tableCount}>
          {filtered.length} of {allQuestions.length} questions
        </span>
      </div>

      <Table
        dataSource={filtered}
        columns={qColumns}
        rowKey="id"
        size="middle"
        pagination={filtered.length > 10 ? { pageSize: 10 } : false}
        className={styles.questionsTable}
        rowClassName={(r) => styles[`qRow_${r.tag}`] ?? ""}
      />
    </Card>
  );
};

// ── KPI card ─────────────────────────────────────────────────────────────────
const KPICard = ({ icon, label, value, sub, color, tooltip }) => (
  <Tooltip title={tooltip} placement="top">
    <div className={styles.kpiCard} style={{ borderTopColor: color }}>
      <div className={styles.kpiIcon} style={{ color }}>{icon}</div>
      <div className={styles.kpiBody}>
        <div className={styles.kpiValue} style={{ color }}>{value}</div>
        <div className={styles.kpiLabel}>{label}</div>
        {sub && <div className={styles.kpiSub}>{sub}</div>}
      </div>
    </div>
  </Tooltip>
);

// ── Questions sub-table (shown inside expanded quiz row) ─────────────────────
const QuestionsSubTable = ({ questions, total, quizId, navigate }) => {
  const qColumns = [
    {
      title: "#",
      key: "idx",
      width: "5%",
      render: (_, __, i) => <span className={styles.qNum}>{i + 1}</span>,
    },
    {
      title: "Question",
      dataIndex: "text",
      key: "text",
      ellipsis: false,
      width: "47%",
      render: (v) => <span className={styles.qText}>{v}</span>,
    },
    {
      title: "Your Answer",
      dataIndex: "yourAnswer",
      key: "yourAnswer",
      width: "18%",
      render: (v, r) => (
        <span className={r.isCorrect ? styles.answerCorrect : styles.answerWrong}>
          {r.isCorrect
            ? <CheckCircleFilled style={{ marginRight: 4, color: "#16a34a" }} />
            : <CloseCircleFilled style={{ marginRight: 4, color: "#ef4444" }} />}
          {v}
        </span>
      ),
    },
    {
      title: "Correct Answer",
      dataIndex: "correctAnswer",
      key: "correctAnswer",
      width: "18%",
      render: (v, r) =>
        r.isCorrect ? (
          <span className={styles.correctAnswerSame}>—</span>
        ) : (
          <span className={styles.correctAnswerDiff}>{v}</span>
        ),
    },
    {
      title: "Status",
      dataIndex: "isCorrect",
      key: "status",
      align: "center",
      width: "6%",
      render: (v) =>
        v ? (
          <CheckCircleFilled className={styles.statusCorrect} />
        ) : (
          <CloseCircleFilled className={styles.statusWrong} />
        ),
    },
    {
      title: "View",
      key: "view",
      align: "center",
      width: "6%",
      render: (_, r) => (
        <Tooltip title="View this question">
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => navigate(`/script-questions/detail/${r.id}`)}
            className={styles.viewLink}
          />
        </Tooltip>
      ),
    },
  ];

  const sampleCount = questions.length;
  const remaining   = total - sampleCount;

  return (
    <div className={styles.questionsExpand}>
      <div className={styles.expandHeader}>
        <div className={styles.expandTitle}>
          <InfoCircleOutlined style={{ color: "#4b93ff" }} />
          <span>
            Showing <strong>{sampleCount}</strong> sample questions
            {remaining > 0 && ` · ${remaining} more in full result`}
          </span>
        </div>
        <Button
          type="primary"
          size="small"
          icon={<LinkOutlined />}
          onClick={() => navigate(`/user/quiz/result/${quizId}`)}
          className={styles.viewResultBtn}
        >
          View Full Result
        </Button>
      </div>

      <Table
        dataSource={questions}
        columns={qColumns}
        rowKey="id"
        size="small"
        pagination={false}
        className={styles.questionsTable}
        rowClassName={(r) => r.isCorrect ? styles.rowCorrect : styles.rowWrong}
      />
    </div>
  );
};

// ── Main quiz history table columns ──────────────────────────────────────────
const buildQuizColumns = (navigate) => [
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    sorter: (a, b) => new Date(a.date) - new Date(b.date),
    defaultSortOrder: "descend",
    width: "11%",
    render: (v) => <span className={styles.dateCell}>{moment(v).format("DD MMM YYYY")}</span>,
  },
  {
    title: "Quiz Name",
    dataIndex: "quizName",
    key: "quizName",
    ellipsis: true,
    width: "30%",
    render: (v) => <span className={styles.quizName}>{v}</span>,
  },
  {
    title: "Score",
    dataIndex: "accuracy",
    key: "accuracy",
    sorter: (a, b) => a.accuracy - b.accuracy,
    width: "22%",
    render: (v) => {
      const color =
        v >= 80 ? "#16a34a" : v >= 60 ? "#4b93ff" : v >= 40 ? "#d97706" : "#ef4444";
      return (
        <div className={styles.scoreCell}>
          <div className={styles.scoreBarTrack}>
            <div className={styles.scoreBarFill} style={{ width: `${v}%`, backgroundColor: color }} />
          </div>
          <span className={styles.scorePct} style={{ color }}>{v}%</span>
        </div>
      );
    },
  },
  {
    title: "Correct / Total",
    key: "correct",
    sorter: (a, b) => a.correct - b.correct,
    align: "center",
    width: "14%",
    render: (_, r) => (
      <span className={styles.correctTotal}>
        <CheckCircleFilled style={{ color: "#16a34a", marginRight: 3 }} />
        {r.correct}
        <span className={styles.separator}>/</span>
        {r.total}
      </span>
    ),
  },
  {
    title: "Status",
    dataIndex: "passed",
    key: "status",
    align: "center",
    width: "10%",
    filters: [
      { text: "Passed", value: true },
      { text: "Failed", value: false },
    ],
    onFilter: (v, r) => r.passed === v,
    render: (v) => (v ? <Tag color="success">Passed</Tag> : <Tag color="error">Failed</Tag>),
  },
  {
    title: "Result",
    key: "result",
    align: "center",
    width: "13%",
    render: (_, r) => (
      <Tooltip title="View full quiz result">
        <Button
          type="link"
          size="small"
          icon={<LinkOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/user/quiz/result/${r.quizId}`);
          }}
          className={styles.resultLink}
        >
          View
        </Button>
      </Tooltip>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
const AnalyticsDetailPage = () => {
  const { attribute = "specialty", item = "" } = useParams();
  const navigate  = useNavigate();
  const user      = useSelector((state) => state?.app?.user?.info);
  const userId    = user?.user_code ?? user?._id;
  const config    = ATTRIBUTE_CONFIG[attribute] ?? ATTRIBUTE_CONFIG.specialty;
  const itemName  = decodeURIComponent(item);

  const [dateRange, setDateRange] = useState(null);

  const queryBody = useMemo(() => {
    const body = { attribute, item: itemName, userId };
    if (dateRange?.[0]) body.startDate = dateRange[0].format("YYYY-MM-DD");
    if (dateRange?.[1]) body.endDate   = dateRange[1].format("YYYY-MM-DD");
    return body;
  }, [attribute, itemName, userId, dateRange]);

  const { data: apiData, isLoading, isFetching } = useGetAnalyticsDetailQuery(
    queryBody,
    { skip: ANALYTICS_USE_MOCK_DATA || !userId, refetchOnMountOrArgChange: true }
  );

  const rawData = ANALYTICS_USE_MOCK_DATA
    ? ANALYTICS_DETAIL_MOCK[attribute]?.[itemName]
    : apiData;

  const loading = ANALYTICS_USE_MOCK_DATA ? false : isLoading || isFetching;

  const data = useMemo(() => {
    if (!rawData) return null;
    if (!ANALYTICS_USE_MOCK_DATA || !dateRange?.[0]) return rawData;
    const start = dateRange[0].clone().startOf("day");
    const end   = dateRange[1].clone().endOf("day");
    const filtered = rawData.quizHistory.filter((q) => {
      const d = moment(q.date);
      return d.isSameOrAfter(start) && d.isSameOrBefore(end);
    });
    if (!filtered.length) return { ...rawData, quizHistory: [], trendOverTime: [], kpis: rawData.kpis };
    const accs = filtered.map((q) => q.accuracy);
    return {
      kpis: {
        totalQuizzes:   filtered.length,
        avgAccuracy:    Math.round(accs.reduce((s, a) => s + a, 0) / accs.length),
        personalBest:   Math.max(...accs),
        totalQuestions: filtered.reduce((s, q) => s + q.total, 0),
        passRate:       Math.round((filtered.filter((q) => q.passed).length / filtered.length) * 100),
        latestScore:    filtered[filtered.length - 1]?.accuracy ?? 0,
      },
      quizHistory:  filtered,
      trendOverTime: filtered.map((q) => ({ date: q.date, accuracy: q.accuracy })),
    };
  }, [rawData, dateRange]);

  const quizHistory  = data?.quizHistory  ?? [];
  const trendData    = data?.trendOverTime ?? [];
  const distribution = useMemo(() => buildDistribution(quizHistory), [quizHistory]);
  const kpis         = data?.kpis;
  const hasData      = quizHistory.length > 0;

  const quizColumns = useMemo(() => buildQuizColumns(navigate), [navigate]);

  // ── Trend chart ────────────────────────────────────────────────────────────
  const trendConfig = useMemo(() => ({
    data: trendData,
    xField: "date",
    yField: "accuracy",
    smooth: true,
    color: config.color,
    point: {
      size: 6,
      shape: "circle",
      style: { fill: config.color, stroke: "#fff", lineWidth: 2 },
    },
    label: {
      formatter: (d) => `${d.accuracy}%`,
      style: { fontSize: 10 },
      offset: 10,
    },
    annotations: [{
      type: "line",
      start: ["min", PASS_THRESHOLD],
      end:   ["max", PASS_THRESHOLD],
      style: { stroke: "#faad14", lineDash: [4, 4], lineWidth: 1.5 },
      text: {
        content: `Pass ${PASS_THRESHOLD}%`,
        offsetY: -6,
        style: { fontSize: 11, fill: "#faad14" },
      },
    }],
    yAxis: { min: 0, max: 100, title: { text: "Accuracy (%)" } },
    xAxis: { label: { formatter: (v) => moment(v).format("DD MMM"), style: { fontSize: 10 } } },
    tooltip: { formatter: (d) => ({ name: "Accuracy", value: `${d.accuracy}%` }) },
    area: { style: { fill: `l(270) 0:${config.color}00 1:${config.color}22` } },
    autoFit: true,
    padding: "auto",
  }), [trendData, config.color]);

  // ── Score distribution chart ───────────────────────────────────────────────
  const distConfig = useMemo(() => ({
    data: distribution,
    xField: "range",
    yField: "count",
    color: ({ range }) => distribution.find((d) => d.range === range)?.color ?? "#4b93ff",
    label: {
      position: "top",
      formatter: (d) => d.count > 0 ? `${d.count}` : "",
      style: { fontSize: 12, fontWeight: 600 },
    },
    yAxis: { title: { text: "Quizzes" }, tickInterval: 1 },
    xAxis: { title: { text: "Score Range" } },
    tooltip: { formatter: (d) => ({ name: "Quizzes", value: d.count }) },
    columnStyle: { radius: [4, 4, 0, 0] },
    autoFit: true,
    padding: "auto",
  }), [distribution]);

  return (
    <PageLayout showSubMenu={false} backIcon={true} label={`By ${config.label}`}>
      <div className={styles.detailPage}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <button className={styles.backBtn} onClick={() => navigate(`/analytics/${attribute}`)}>
              <ArrowLeftOutlined /> Back
            </button>
            <div className={styles.headerIcon} style={{ color: config.color }}>{config.icon}</div>
            <div>
              <Title level={4} className={styles.pageTitle}>{itemName}</Title>
              <Text type="secondary" className={styles.pageDesc}>
                {config.label} · {kpis?.totalQuizzes ?? 0} quiz{kpis?.totalQuizzes !== 1 ? "zes" : ""}
                {kpis?.totalQuestions ? ` · ${kpis.totalQuestions} questions` : ""}
              </Text>
            </div>
          </div>
          <div className={styles.headerRight}>
            <FilterOutlined className={styles.filterIcon} />
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              presets={DATE_PRESETS}
              format="DD MMM YYYY"
              allowClear
              placeholder={["Start date", "End date"]}
              style={{ minWidth: 240 }}
            />
          </div>
        </div>

        {/* ── KPI strip ──────────────────────────────────────────────── */}
        {loading ? (
          <div className={styles.kpiStrip}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className={styles.kpiCard}>
                <Skeleton active paragraph={{ rows: 2 }} title={false} />
              </div>
            ))}
          </div>
        ) : kpis ? (
          <div className={styles.kpiStrip}>
            <KPICard
              icon={<CheckCircleOutlined />}
              label="Total Quizzes"
              value={kpis.totalQuizzes}
              sub={`${kpis.totalQuestions} questions`}
              color={config.color}
              tooltip="Total quizzes completed for this item in the selected period"
            />
            <KPICard
              icon={<StarFilled />}
              label="Avg Accuracy"
              value={`${kpis.avgAccuracy}%`}
              sub={kpis.avgAccuracy >= PASS_THRESHOLD ? "Above pass threshold" : "Below pass threshold"}
              color={kpis.avgAccuracy >= PASS_THRESHOLD ? "#16a34a" : "#ef4444"}
              tooltip="Average accuracy across all quizzes for this item"
            />
            <KPICard
              icon={<TrophyOutlined />}
              label="Personal Best"
              value={`${kpis.personalBest}%`}
              sub="Highest single quiz"
              color="#f59e0b"
              tooltip="Your best single-quiz score for this item"
            />
            <KPICard
              icon={<CheckCircleFilled style={{ color: "#16a34a" }} />}
              label="Pass Rate"
              value={`${kpis.passRate}%`}
              sub={`${Math.round((kpis.passRate / 100) * kpis.totalQuizzes)} / ${kpis.totalQuizzes} passed`}
              color={kpis.passRate >= 60 ? "#16a34a" : "#d97706"}
              tooltip={`Percentage of quizzes where you scored ≥ ${PASS_THRESHOLD}%`}
            />
            <KPICard
              icon={<CheckCircleOutlined />}
              label="Total Questions"
              value={kpis.totalQuestions}
              sub={`${kpis.totalQuizzes} attempts`}
              color="#6366f1"
              tooltip="Total questions answered across all quizzes for this item"
            />
            <KPICard
              icon={<StarFilled />}
              label="Latest Score"
              value={`${kpis.latestScore}%`}
              sub="Most recent quiz"
              color={kpis.latestScore >= PASS_THRESHOLD ? "#16a34a" : "#ef4444"}
              tooltip="Score in your most recent quiz for this item"
            />
          </div>
        ) : null}

        {loading ? (
          <div className={styles.spinnerCenter}><Spin size="large" /></div>
        ) : !hasData ? (
          <div className={styles.emptyCenter}>
            <Empty description={`No quiz data found for "${itemName}"`} />
          </div>
        ) : (
          <>
            {/* ── Charts row ────────────────────────────────────────── */}
            <div className={styles.chartsRow}>
              <Card
                className={styles.chartCard}
                title={
                  <div className={styles.cardTitle}>
                    <span>Accuracy Per Quiz Attempt</span>
                    <Tooltip title="Each point = one quiz attempt, ordered by date. Yellow dashed = pass threshold (60%).">
                      <InfoCircleOutlined className={styles.infoIcon} />
                    </Tooltip>
                  </div>
                }
              >
                <div className={styles.chartArea}>
                  {trendData.length ? <Line {...trendConfig} /> : <Empty description="No trend data" />}
                </div>
              </Card>

              <Card
                className={styles.chartCard}
                title={
                  <div className={styles.cardTitle}>
                    <span>Score Distribution</span>
                    <Tooltip title="How many of your quizzes fell in each score band. Green = passing.">
                      <InfoCircleOutlined className={styles.infoIcon} />
                    </Tooltip>
                  </div>
                }
              >
                <div className={styles.chartArea}>
                  <Column {...distConfig} />
                </div>
              </Card>
            </div>

            {/* ── Questions analysis ───────────────────────────────── */}
            <QuestionsAnalysis quizHistory={quizHistory} navigate={navigate} />

            {/* ── Quiz history table ────────────────────────────────── */}
            <Card
              className={styles.tableCard}
              title={
                <div className={styles.cardTitle}>
                  <span>Quiz History</span>
                  <span className={styles.quizCount}>{quizHistory.length} quizzes</span>
                  <Tooltip title="Expand a row to see individual questions and your answers. Click 'View' to open a question or quiz result.">
                    <InfoCircleOutlined className={styles.infoIcon} />
                  </Tooltip>
                </div>
              }
            >
              <Table
                dataSource={quizHistory}
                columns={quizColumns}
                rowKey={(r) => r.quizId ?? `${r.date}-${r.quizName}`}
                size="middle"
                pagination={quizHistory.length > 8 ? { pageSize: 8 } : false}
                className={styles.quizTable}
                expandable={{
                  expandedRowRender: (record) => (
                    <QuestionsSubTable
                      questions={record.questions ?? []}
                      total={record.total}
                      quizId={record.quizId}
                      navigate={navigate}
                    />
                  ),
                  rowExpandable: (record) => (record.questions?.length ?? 0) > 0,
                  expandRowByClick: false,
                  columnWidth: 40,
                }}
              />
            </Card>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default AnalyticsDetailPage;
